/**
 * KS · Subscription Product
 * Front-end logic for the ks-subscription-product.liquid section.
 *
 * Responsibilities:
 *   - Quantity stepper (with per-customer limit)
 *   - Purchase-type toggle (one-time vs subscribe-save)
 *   - Recharge selling-plan dropdown (enabled only when subscribe-save selected)
 *   - Live Total recalculation (unit price × quantity, currency-formatted)
 *   - Sticky mobile ATC bar sync
 *   - ATC form submit (POST /cart/add.js with id, quantity, selling_plan)
 *
 * Owner: Katie Spaller.
 */
(function () {
  'use strict';

  const root = document.querySelector('.ks-subscription-product');
  if (!root) return;

  const qtyLimit = parseInt(root.dataset.qtyLimit, 10) || 6;
  const oneTimePriceCents = parseInt(root.dataset.oneTimePrice, 10) || 0;
  let subscribePriceCents = parseInt(root.dataset.subscribePrice, 10) || oneTimePriceCents;
  const hasSellingPlan = root.dataset.hasSellingPlan === 'true';

  const form = root.querySelector('[data-ks-sub-form]');
  const qtyInput = root.querySelector('[data-ks-qty-input]');
  const qtyDec = root.querySelector('.qty-stepper [data-action="dec"]');
  const qtyInc = root.querySelector('.qty-stepper [data-action="inc"]');
  const totalEl = root.querySelector('[data-ks-total]');
  const atcBtn = root.querySelector('[data-ks-atc]');
  const purchaseRadios = root.querySelectorAll('input[name="purchase-option"]');
  const sellingPlanSelect = root.querySelector('[data-ks-selling-plan]');
  const subscribePriceEl = root.querySelector('[data-ks-subscribe-price]');

  // Sticky ATC
  const stickyEl = document.getElementById('ks-subscription-sticky-atc');
  const stickyTotal = stickyEl ? stickyEl.querySelector('[data-sticky-total]') : null;
  const stickyAtcBtn = stickyEl ? stickyEl.querySelector('[data-sticky-atc]') : null;

  // ---------- helpers ----------

  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    // Use a basic USD format; theme renders Liquid-formatted prices on initial paint
    // and we mirror that here for client-side updates.
    return '$' + parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function getCurrentPurchaseType() {
    const checked = root.querySelector('input[name="purchase-option"]:checked');
    return checked ? checked.value : 'one-time';
  }

  function getCurrentUnitPriceCents() {
    return getCurrentPurchaseType() === 'subscribe-save'
      ? subscribePriceCents
      : oneTimePriceCents;
  }

  function getQty() {
    const n = parseInt(qtyInput.value, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function clampQty(n) {
    if (n < 1) return 1;
    if (n > qtyLimit) return qtyLimit;
    return n;
  }

  // ---------- update routines ----------

  function updateTotal() {
    const totalCents = getCurrentUnitPriceCents() * getQty();
    const formatted = formatMoney(totalCents);
    if (totalEl) totalEl.textContent = formatted;
    if (stickyTotal) stickyTotal.textContent = formatted;
  }

  function updateSubscribePriceLabel() {
    if (subscribePriceEl) {
      subscribePriceEl.textContent = formatMoney(subscribePriceCents);
    }
  }

  function syncSellingPlanState() {
    if (!sellingPlanSelect) return;
    const isSubscribe = getCurrentPurchaseType() === 'subscribe-save';
    sellingPlanSelect.disabled = !isSubscribe;
  }

  // ---------- event handlers ----------

  function onQtyClick(delta) {
    return function () {
      const next = clampQty(getQty() + delta);
      qtyInput.value = next;
      updateTotal();
    };
  }

  function onPurchaseTypeChange() {
    syncSellingPlanState();
    updateTotal();
  }

  function onSellingPlanChange() {
    const opt = sellingPlanSelect.options[sellingPlanSelect.selectedIndex];
    if (!opt) return;
    const priceCents = parseInt(opt.getAttribute('data-subscription-price-cents'), 10);
    if (!isNaN(priceCents)) {
      subscribePriceCents = priceCents;
      updateSubscribePriceLabel();
      updateTotal();
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    if (atcBtn.disabled) return;

    const variantInput = form.querySelector('[data-ks-variant-input]');
    const variantId = parseInt(variantInput.value, 10);
    const qty = getQty();
    if (!variantId) return;

    // Build the /cart/add.js JSON item — matches the GP Shop / cw-sweeps pattern
    // so the theme's existing openCartDrawer() / .cart-count refresh works.
    const item = { id: variantId, quantity: qty };
    if (getCurrentPurchaseType() === 'subscribe-save' && sellingPlanSelect && sellingPlanSelect.value) {
      item.selling_plan = sellingPlanSelect.value;
    }

    // Disable both main + sticky buttons during the request
    const stickyBtnEl = stickyEl && stickyEl.querySelector('[data-sticky-atc]');
    const buttons = [atcBtn, stickyBtnEl].filter(Boolean);
    buttons.forEach(function (b) {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
      const s = b.querySelector('span') || b;
      s.dataset.ksOriginal = s.textContent;
      s.textContent = 'Adding…';
    });

    function restoreButtons() {
      buttons.forEach(function (b) {
        b.disabled = false;
        b.removeAttribute('aria-busy');
        const s = b.querySelector('span') || b;
        if (s.dataset.ksOriginal) {
          s.textContent = s.dataset.ksOriginal;
          delete s.dataset.ksOriginal;
        }
      });
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error((err && err.description) || ('Add to cart failed: ' + r.status));
          }, function () { throw new Error('Add to cart failed: ' + r.status); });
        }
        return r.json();
      })
      .then(function () {
        // Refresh the header cart count badge directly (works even if openCartDrawer is missing)
        if (window.jQuery) {
          window.jQuery.getJSON('/cart.js', function (cart) {
            window.jQuery('.cart-count').text(cart.item_count);
          });
        } else {
          fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('.cart-count').forEach(function (el) {
                el.textContent = cart.item_count;
              });
            })
            .catch(function () { /* non-fatal */ });
        }
        restoreButtons();
        // Open the theme's side cart drawer (defined in cw-custom-dev.js.liquid).
        // Falls back to navigating to /cart if the helper isn't present.
        if (typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        restoreButtons();
        // eslint-disable-next-line no-console
        console.error('[ks-subscription-product] ATC error', err);
        alert((err && err.message) || 'Sorry, something went wrong adding this to your cart. Please try again.');
      });
  }

  // Sticky ATC click — runs the same submit path as the main ATC.
  // Synthesizes a submit event so onSubmit() handles button state, fetch, and drawer open.
  function onStickyAtcClick(e) {
    if (e) e.preventDefault();
    if (!form) return;
    onSubmit({ preventDefault: function () {} });
  }

  // Thumbnail gallery — clicking a thumb swaps the main image
  function initThumbnailGallery() {
    const mainImg = root.querySelector('[data-ks-main-img]');
    const mainWrap = root.querySelector('[data-ks-main-image]');
    const thumbs = root.querySelectorAll('[data-ks-thumb]');
    if (!mainImg || !thumbs.length) return;

    function activate(thumb) {
      const largeSrc = thumb.getAttribute('data-large-src');
      const alt = thumb.getAttribute('data-alt') || mainImg.getAttribute('data-default-alt') || '';
      if (!largeSrc) return;

      // Skip if already showing this image
      if (mainImg.getAttribute('src') === largeSrc) {
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
        return;
      }

      // Fade-swap
      if (mainWrap) mainWrap.classList.add('is-swapping');
      const preloader = new Image();
      preloader.onload = function () {
        mainImg.setAttribute('src', largeSrc);
        mainImg.setAttribute('alt', alt);
        if (mainWrap) mainWrap.classList.remove('is-swapping');
      };
      preloader.onerror = function () {
        if (mainWrap) mainWrap.classList.remove('is-swapping');
      };
      preloader.src = largeSrc;

      thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { activate(thumb); });
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate(thumb);
        }
      });
    });
  }

  // Sticky bar visibility (mobile only — appears after user scrolls past buy box)
  function initStickyObserver() {
    if (!stickyEl) return;
    const buyBox = root.querySelector('#ks-subscription-shop');
    if (!buyBox || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show sticky when buy box has scrolled out of view
        const visible = !entry.isIntersecting;
        stickyEl.setAttribute('aria-hidden', visible ? 'false' : 'true');
        stickyEl.classList.toggle('is-visible', visible);
      });
    }, { threshold: 0.1 });

    observer.observe(buyBox);
  }

  // ---------- wire up ----------

  if (qtyDec) qtyDec.addEventListener('click', onQtyClick(-1));
  if (qtyInc) qtyInc.addEventListener('click', onQtyClick(1));

  purchaseRadios.forEach(function (radio) {
    radio.addEventListener('change', onPurchaseTypeChange);
  });

  if (sellingPlanSelect) {
    sellingPlanSelect.addEventListener('change', onSellingPlanChange);
  }

  if (form) form.addEventListener('submit', onSubmit);
  if (stickyAtcBtn) stickyAtcBtn.addEventListener('click', onStickyAtcClick);

  // Initial sync
  syncSellingPlanState();
  updateTotal();
  initStickyObserver();
  initThumbnailGallery();
})();
